import { getSocket } from '@/services/socket/websocket.service.js'
import firebase from 'firebase/compat/app'
import 'firebase/compat/auth'
import { joinUserNotificationChannel as joinUserNotifications } from '@/services/socket/userNotificationSocket.service.js'
import { joinAdminCallsChannel } from '@/services/socket/admin.socket.service.js'

// Keep track of registered event names to properly clean them up
const registeredEventNames = new Set()

/**
 * Join all existing conversations via sockets
 * @param {Array} conversations - List of conversations
 * @param {Object} user - Current user
 */
export async function joinAllExistingConversations(conversations, user) {
    // Renamed slightly for clarity, only joins existing ones now
    const idToken = await firebase.auth().currentUser.getIdToken(true)
    const socket = getSocket()

    if (socket && conversations && conversations.length > 0) {
        console.log(
            `Joining ${conversations.length} existing conversation rooms...`
        )
        conversations.forEach((conversation) => {
            socket.emit(
                'join conversation',
                conversation._id,
                user.email,
                idToken
            )
        })
    } else if (!socket) {
        console.log('Socket not found for joinAllExistingConversations')
    }
}

/**
 * Join a specific conversation via socket
 * @param {String} conversationId - ID of the conversation to join
 * @param {Object} user - Current user
 */
export async function joinNewConversation(conversationId, user) {
    console.log('joinNewConversation', conversationId)
    const socket = getSocket()
    const idToken = await firebase.auth().currentUser.getIdToken(true)

    if (socket) {
        socket.emit('join conversation', conversationId, user.email, idToken)
    } else {
        console.log('socket not found')
    }
}

/**
 * Setup reconnection handler to re-join all conversations and user channel
 * @param {Array} conversations - List of conversations (reactive ref or getter recommended)
 * @param {Object} user - Current user
 */
export function setupReconnectionHandler(conversations, user) {
    const socket = getSocket()
    const eventName = 'connect' // Specific event for this handler
    if (socket) {
        // Check if this specific handler hasn't been added yet
        if (!registeredEventNames.has(eventName + '.rejoinChannels')) {
            // Use unique key for tracking
            const handler = async () => {
                if (socket.recovered) {
                    console.log(
                        'Connection state recovered, no need to rejoin channels manually.'
                    )
                    return
                }
                // Made async to await joinUserNotifications
                console.log(
                    'Reconnected to the server (rejoinChannels handler), rejoining channels manually.'
                )
                // Re-join user notification channel (now imported)
                await joinUserNotifications()
                // Re-join existing conversation rooms (pass the current value if reactive)
                const currentConversations =
                    typeof conversations === 'function'
                        ? conversations()
                        : conversations
                // This function itself is async, so await it if it does async work and needs to complete first
                await joinAllExistingConversations(currentConversations, user)

                if (user?.isAdmin) {
                    console.log('Re-joining admin channels...')
                    await joinAdminCallsChannel()
                }
            }
            socket.on(eventName, handler)
            registeredEventNames.add(eventName + '.rejoinChannels') // Track this specific handler
        }
    } else {
        console.log('Socket not found for setupReconnectionHandler')
    }
}

/**
 * Setup handler to update messages when socket reconnects
 * @param {Object} currentConversation - The current active conversation (reactive ref or getter recommended)
 * @param {Function} syncCallback - Function to call to sync messages
 */
export function setupMessageReconnectionHandler(
    currentConversation,
    syncCallback
) {
    const socket = getSocket()
    const connectEventName = 'connect'
    const disconnectEventName = 'disconnect'

    if (socket) {
        // Connect handler for syncing messages
        if (!registeredEventNames.has(connectEventName + '.updateMessages')) {
            const handler = () => {
                if (socket.recovered) {
                    console.log(
                        'Connection state recovered, no need to sync messages manually.'
                    )
                    return
                }
                console.log(
                    'Reconnected to the server (updateMessages handler)'
                )
                const currentConvo =
                    typeof currentConversation === 'function'
                        ? currentConversation()
                        : currentConversation
                if (currentConvo?._id) {
                    // Check if currentConversation object and _id exist
                    syncCallback(currentConvo._id)
                }
            }
            socket.on(connectEventName, handler)
            registeredEventNames.add(connectEventName + '.updateMessages')
        }

        // Disconnect handler
        if (!registeredEventNames.has(disconnectEventName)) {
            const handler = () => {
                console.log(
                    'Disconnected from server, will attempt to reconnect...'
                )
            }
            socket.on(disconnectEventName, handler)
            registeredEventNames.add(disconnectEventName)
        }
    } else {
        console.log('Socket not found for setupMessageReconnectionHandler')
    }
}

/**
 * Setup all general socket listeners for conversation events.
 * Callbacks provided (e.g., onNewMessage) will receive raw socket data.
 * The caller (the Store) is responsible for updating its state and IndexedDB.
 * @param {Object} options - Options containing callbacks { onNewMessage, onMessageReceived, ... }
 */
export function setupSocketListeners(callbacks) {
    const socket = getSocket()
    if (!socket) {
        console.error(
            'SOCKET.SERVICE: Cannot setup listeners: Socket not available.'
        )
        return
    }
    console.log(
        'SOCKET.SERVICE: Setting up listeners for events:',
        Object.keys(callbacks)
    )

    for (const [eventName, handler] of Object.entries(callbacks)) {
        if (handler && typeof handler === 'function') {
            // Only register if not already tracked (prevents duplicates within a session)
            if (!registeredEventNames.has(eventName)) {
                // Wrap handler to add error catching
                const wrappedHandler = (...args) => {
                    try {
                        handler(...args)
                    } catch (error) {
                        console.error(
                            `SOCKET.SERVICE: Error processing socket event '${eventName}':`,
                            error
                        )
                    }
                }
                socket.on(eventName, wrappedHandler)
                registeredEventNames.add(eventName) // Track that we added a listener for this event
            }
        } else if (callbacks.hasOwnProperty(eventName)) {
            // Log if a callback was expected but not provided or not a function
            console.warn(
                `SOCKET.SERVICE: Callback for socket event '${eventName}' is missing or not a function.`
            )
        }
    }
}

/**
 * Emit an event to notify that the user is typing
 * @param {String} conversationId - The conversation ID
 */
export function emitTyping(conversationId) {
    const socket = getSocket()
    if (socket) {
        socket.emit('typing', { conversationId })
    }
}

/**
 * Emit an event to notify that the user stopped typing
 * @param {String} conversationId - The conversation ID
 */
export function emitStopTyping(conversationId) {
    const socket = getSocket()
    if (socket) {
        socket.emit('stop typing', { conversationId })
    }
}

/**
 * Clean up ALL socket listeners managed by this service.
 */
export function cleanupSockets() {
    const socket = getSocket()

    console.log('SOCKET.SERVICE: Cleaning up socket listeners...')

    // Even if socket is gone, we should still clear the tracking set
    const eventsToClean = Array.from(registeredEventNames)

    if (socket) {
        console.log(
            'SOCKET.SERVICE: Removing listeners for events:',
            eventsToClean
        )

        // Remove events only if socket exists
        eventsToClean.forEach((eventName) => {
            try {
                // Remove *all* listeners for this event name added via this service
                socket.off(eventName)
                console.log(
                    `SOCKET.SERVICE: Removed listeners for ${eventName}`
                )
            } catch (error) {
                console.error(
                    `SOCKET.SERVICE: Error removing listener for ${eventName}:`,
                    error
                )
            }
        })
    } else {
        console.log(
            'SOCKET.SERVICE: Socket instance not found, just clearing event tracking'
        )
    }

    // Always clear the tracking set
    registeredEventNames.clear()
    console.log('SOCKET.SERVICE: Socket listener tracking cleared.')
}

export default {
    joinAllExistingConversations, // Updated name
    joinNewConversation,
    setupReconnectionHandler,
    setupMessageReconnectionHandler,
    setupSocketListeners,
    emitTyping,
    emitStopTyping,
    cleanupSockets, // Ensure cleanup is exported
}
